import bem from 'easy-bem';
import { render, h } from 'preact';
import {
  StoreProvider,
  CheckoutProvider,
} from '@primer-io/shared-library/contexts';

import {
  InputValidationError,
  PaymentMethodToken,
  PrimerVaultManager,
  SupportedLocale,
  VaultManagerOptions,
} from '../../types';

import { Vault } from '../Vault';

import { locateElement } from '../../utils/dom';
import { Translations } from '../../assets/translations/Translations';
import { getAttribute, setValue } from '../DomUtilities';
import { ElementID, Scene } from '../../enums/Checkout';

import { PaymentMethods } from '../../payment-methods/PaymentMethods';
import { BasePaymentMethod } from '../../payment-methods/BasePaymentMethod';
import { VaultManagerPaymentMethodConfig } from '../VaultManagerPaymentMethodConfig';
import { IFrameEventType } from '../../core/IFrameEventType';
import { IFrameMessagePayload } from '../../core/IFrameMessage';
import { ClientContext } from '../../core/ClientContextFactory';

import { ErrorCode, PrimerClientError } from '../../errors';
import { createVaultManagerViewUtils } from '../ui/ViewUtils';

import VaultRoot from '../../scenes/VaultRoot';
import { SceneTransition } from '../SceneTransition';
import { createStore } from '../../store/CheckoutStoreFactory';
import { isCardholderNameRequired } from '../../payment-methods/credit-card/utils';
import { getSystemLocale } from '../../utils/getSystemLocale';
import { PaymentInstrumentType } from '../../enums/Tokens';
import { CreditCard } from '../../payment-methods/credit-card/CreditCard';
import { teardownUI } from '../Teardown';

const renderRoot = ({
  options,
  checkout,
  viewUtils,
  context,
  transitions,
  store,
  root,
}: {
  options;
  checkout;
  viewUtils;
  context?;
  transitions;
  store;
  root;
}) =>
  render(
    <CheckoutProvider
      value={{
        options,
        checkout,
        viewUtils,
        context,
        transitions,
        className: bem('PrimerCheckout'),
      }}
    >
      <StoreProvider value={store}>
        <VaultRoot />
      </StoreProvider>
    </CheckoutProvider>,
    root,
  );

export class VaultManager {
  static async create(
    getContext: () => Promise<ClientContext>,
    options: VaultManagerOptions,
  ): Promise<PrimerVaultManager> {
    const parent = locateElement(options.container);
    const cardOptions = options.card || {};
    const cardholderNameRequired = isCardholderNameRequired(cardOptions);

    if (!parent) {
      return onError(
        `Attempted to mount vault UI at "${options.container}" but the element could not be found`,
      );
    }

    const root = document.createElement('div');
    parent.prepend(root);

    ///////////////////////////////////////////
    // Context
    ///////////////////////////////////////////
    const contextPromise = getContext();

    ///////////////////////////////////////////
    // Render
    ///////////////////////////////////////////
    const store = createStore();
    store.setLocale(options.locale ?? (getSystemLocale() as SupportedLocale));

    const viewUtils = createVaultManagerViewUtils(store, options);

    const validateForm = async () => {
      let valid = false;
      const validationErrors: InputValidationError[] = [];

      const cardValidation = await paymentMethods.card.validate();

      let uxValid = true;

      if (cardholderNameRequired) {
        const value = getAttribute(ElementID.CARDHOLDER_NAME_INPUT, 'value');
        const error = value ? null : store.getTranslations()?.cardNameRequired;
        if (error) {
          validationErrors.push({
            name: 'cardholderName',
            error: 'cardNameRequired',
            message: error,
          });
        }
        viewUtils.fieldUtils.setErrorState(
          ElementID.CARDHOLDER_NAME,
          error ?? null,
        );
        uxValid = !!value;
      }

      valid = cardValidation.valid && uxValid;

      return {
        valid,
        validationErrors: [
          ...cardValidation.validationErrors,
          ...validationErrors,
        ],
      };
    };

    async function tokenizeCard() {
      const { valid } = await validateForm();

      if (!valid) {
        return;
      }

      await paymentMethods.card.tokenize();
    }

    function teardown() {
      if (context.session.isTeardown) {
        throw new Error('Checkout has already been torn down');
      }

      context.session.isTeardown = true;

      teardownUI(root);
    }

    const checkout = {
      validate: validateForm,
      tokenize: tokenizeCard,
      teardown,
    };
    const transitions = new SceneTransition();
    renderRoot({ options, checkout, viewUtils, transitions, store, root });

    ///////////////////////////////////////////
    // State
    ///////////////////////////////////////////
    const translation = Translations.get(options.locale);
    store.setIsLoading(true);

    store.produceState((draft) => {
      draft.translation = translation;
      draft.options.showSavePaymentMethod = false;
      draft.form.inputLabelsVisible = options.form?.inputLabelsVisible ?? true;
      // Remove check once purchaseInfo is removed
      if (options.purchaseInfo) {
        console.warn(
          'purchaseInfo has been deprecated, please use orderDetails instead',
        );
        const updatedOrderDetails = {
          currencyCode: options.purchaseInfo.totalAmount.currency,
        };
        draft.options.orderDetails = updatedOrderDetails ?? null;
      } else {
        draft.options.orderDetails = options.orderDetails ?? null;
      }
    });

    ///////////////////////////////////////////
    // Vault & Payment Methods
    ///////////////////////////////////////////
    const context = await contextPromise;
    const processVault = async () => {
      const vault = new Vault(context);
      store.setVault(vault);
      const vaulted = await vault.fetch();
      vaulted.forEach((pm) => {
        store.addVault(pm);
      });
    };

    const config = VaultManagerPaymentMethodConfig.create(
      context,
      store,
      viewUtils,
      {
        ...options,
        vaultOnly: true,
        onTokenizeError(err: PrimerClientError) {
          switch (err.code) {
            case ErrorCode.DUPLICATE_PAYMENT_METHOD_ERROR:
              store.setErrorMessage(
                translation?.duplicatePaymentMethodError ??
                  'duplicatePaymentMethodError',
              );

              break;
            default:
              break;
          }

          if (options.onTokenizeError) {
            options.onTokenizeError(err);
          }
        },
        onTokenizeSuccess(data: PaymentMethodToken) {
          options.onTokenizeSuccess(data);
          store.addVault(data);

          // Reset
          if (data.paymentInstrumentType === PaymentInstrumentType.CARD) {
            (paymentMethods.card as CreditCard).reset();
          }
        },
      },
    );

    let paymentMethods: Record<string, BasePaymentMethod> = {};
    const processPaymentMethods = async () => {
      paymentMethods = await PaymentMethods.create(
        context,
        config,
        store,
        viewUtils.styleManager,
      );
      store.setPaymentMethods(paymentMethods);
    };
    await Promise.all([processVault(), processPaymentMethods()]);

    ///////////////////////////////////////////
    // End
    ///////////////////////////////////////////
    context.messageBus.on(
      IFrameEventType.CARDHOLDER_NAME,
      (e: IFrameMessagePayload<string>) => {
        setValue(ElementID.CARDHOLDER_NAME_INPUT, e.payload);
      },
    );

    store.setIsLoading(false);
    store.setScene(Scene.MANAGE_PAYMENT_METHODS);

    return checkout;
  }
}

function onError(message: string): PrimerVaultManager {
  window.console.error(message);
  return {
    teardown: () => undefined,
    tokenize: () => Promise.resolve(),
    validate: () => Promise.resolve({ valid: false, validationErrors: [] }),
  };
}
